import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton,
  Button, CircularProgress, Fade, Fab, Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPaperPlane, faClipboard, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const API_BASE = 'https://feescrita-138417386089.us-central1.run.app';
const PIX_KEY = '3657feeb-4c56-4beb-9296-8ab5960f44b0';

const PRAYER_START = '[ORAÇÃO_INICIO]';
const PRAYER_END = '[ORAÇÃO_FIM]';

function parsePrayerFromText(text) {
  const start = text.indexOf(PRAYER_START);
  const end = text.indexOf(PRAYER_END);
  if (start !== -1 && end !== -1) {
    const before = text.substring(0, start).trim();
    const prayer = text.substring(start + PRAYER_START.length, end).trim();
    return { before, prayer, hasPrayer: true };
  }
  return { before: text, prayer: null, hasPrayer: false };
}

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: 'Olá! Sou o assistente de oração do Fé Escrita. É uma alegria ter você aqui! 💙\n\nComo posso orar por você hoje?',
};

const PrayerChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPrayer, setCurrentPrayer] = useState(null);
  const [phase, setPhase] = useState('chat'); // chat | prayer_shown | donation | pix | done
  const [donationAmount, setDonationAmount] = useState(null);
  const [prayerCopied, setPrayerCopied] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, phase, currentPrayer]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: 'user', content: inputValue.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/prayer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages }),
      });

      const data = await response.json();
      const reply = data.reply || 'Desculpe, algo deu errado. Vamos tentar novamente?';
      const parsed = parsePrayerFromText(reply);

      if (parsed.hasPrayer) {
        if (parsed.before) {
          setMessages(prev => [...prev, { role: 'assistant', content: parsed.before }]);
        }
        setCurrentPrayer(parsed.prayer);
        setPhase('prayer_shown');
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Estou com dificuldades no momento. Por favor, tente novamente. 🙏' },
      ]);
    }

    setIsLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopyPrayer = async () => {
    try {
      await navigator.clipboard.writeText(currentPrayer);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = currentPrayer;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setPrayerCopied(true);
    setTimeout(() => setPrayerCopied(false), 2000);
  };

  const handleSharePrayer = () => {
    const msg = `🙏 Oração especial do Fé Escrita:\n\n${currentPrayer}\n\n✝️ feescrita.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  const handleSkipDonate = () => {
    setPhase('done');
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: 'Tudo bem! O importante é que você foi abençoado(a). 💙\n\nVolte sempre que precisar de oração. Estou aqui por você! 🙏' },
    ]);
  };

  const handleContinuePraying = () => {
    setCurrentPrayer(null);
    setPhase('chat');
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: 'Claro! Por qual outra situação posso orar com você? 🙏' },
    ]);
  };

  const handleCopyPix = async () => {
    try {
      await navigator.clipboard.writeText(PIX_KEY);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = PIX_KEY;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setPixCopied(true);
    setTimeout(() => setPixCopied(false), 3000);
  };

  const handleDonationDone = () => {
    setPhase('done');
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: 'Muito obrigado pela sua generosidade! 🙏\n\nQue Deus abençoe ricamente sua vida. Volte sempre! 💙' },
    ]);
  };

  const renderMessage = (msg, index) => {
    const isUser = msg.role === 'user';
    return (
      <Box key={index} sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1 }}>
        <Box
          sx={{
            maxWidth: '85%',
            backgroundColor: isUser ? '#4A90D9' : '#f0f0f0',
            color: isUser ? 'white' : 'text.primary',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            px: 2,
            py: 1.2,
            fontSize: '13px',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {msg.content}
        </Box>
      </Box>
    );
  };

  const renderPrayerCard = () => (
    <Box sx={{ mb: 1 }}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          backgroundColor: '#fef9f0',
          borderLeft: '4px solid #c9a84c',
          borderRadius: '10px',
          mx: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.8, fontFamily: 'Georgia, serif', color: '#333' }}
        >
          {currentPrayer}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCopyPrayer}
            startIcon={<FontAwesomeIcon icon={prayerCopied ? faCheck : faClipboard} size="xs" />}
            sx={{ fontSize: '11px', color: prayerCopied ? 'green' : undefined, borderColor: prayerCopied ? 'green' : undefined }}
          >
            {prayerCopied ? 'Copiado!' : 'Salvar'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSharePrayer}
            startIcon={<FontAwesomeIcon icon={faWhatsapp} size="xs" />}
            sx={{ fontSize: '11px', color: '#25D366', borderColor: '#25D366' }}
          >
            Compartilhar
          </Button>
        </Box>
      </Paper>

      {phase === 'prayer_shown' && (
        <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#e8f4fd', borderRadius: '10px', mx: 0.5 }}>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 1.5, color: '#555', fontSize: '12px' }}>
            Se esta oração tocou seu coração, você pode contribuir com o ministério Fé Escrita. 💙
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => setPhase('donation')}
              sx={{ backgroundColor: '#4A90D9', fontSize: '12px' }}
            >
              💝 Contribuir
            </Button>
            <Button size="small" variant="text" onClick={handleSkipDonate} sx={{ fontSize: '11px', color: '#999' }}>
              Talvez depois
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button size="small" variant="text" onClick={handleContinuePraying} sx={{ fontSize: '11px' }}>
              🙏 Orar por outro motivo
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderDonationPanel = () => (
    <Box sx={{ p: 1.5, backgroundColor: '#f9f9f9', borderRadius: '10px', mx: 0.5, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ textAlign: 'center', mb: 1, fontWeight: 'bold', fontSize: '13px' }}>
        💝 Contribua com o ministério
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1.5 }}>
        {[10, 25, 50].map(amount => (
          <Button
            key={amount}
            size="small"
            variant={donationAmount === amount ? 'contained' : 'outlined'}
            onClick={() => setDonationAmount(amount)}
            sx={{ minWidth: '56px', fontWeight: 'bold', fontSize: '12px' }}
          >
            R${amount}
          </Button>
        ))}
      </Box>
      {donationAmount && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setPhase('pix')}
            sx={{ backgroundColor: '#32BCAD', fontSize: '12px' }}
          >
            📱 Pix
          </Button>
          <Tooltip title="Em breve">
            <span>
              <Button size="small" variant="outlined" disabled sx={{ fontSize: '12px' }}>
                💳 Cartão
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button size="small" variant="text" onClick={handleSkipDonate} sx={{ fontSize: '11px', color: '#bbb' }}>
          Talvez depois
        </Button>
      </Box>
    </Box>
  );

  const renderPixPanel = () => (
    <Box sx={{ p: 1.5, backgroundColor: '#f0f9f7', borderRadius: '10px', mx: 0.5, mb: 1, textAlign: 'center' }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold', fontSize: '13px' }}>
        📱 Pix — R${donationAmount}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: '#555', fontSize: '11px' }}>
        Copie a chave Pix e realize a transferência no seu banco:
      </Typography>
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px dashed #32BCAD',
          borderRadius: '8px',
          p: 1.2,
          mb: 1.2,
          fontFamily: 'monospace',
          fontSize: '12px',
          wordBreak: 'break-all',
        }}
      >
        {PIX_KEY}
      </Box>
      <Button
        size="small"
        variant="contained"
        onClick={handleCopyPix}
        startIcon={<FontAwesomeIcon icon={pixCopied ? faCheck : faClipboard} size="xs" />}
        sx={{ backgroundColor: pixCopied ? 'green' : '#32BCAD', mb: 1.2, fontSize: '11px' }}
      >
        {pixCopied ? 'Chave copiada!' : 'Copiar chave Pix'}
      </Button>
      <Box>
        <Button size="small" variant="text" onClick={handleDonationDone} sx={{ fontSize: '11px' }}>
          ✅ Já paguei
        </Button>
      </Box>
    </Box>
  );

  const showInput = phase === 'chat' || phase === 'done';

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
      {!isOpen && (
        <Tooltip title="Assistente de Oração" placement="left">
          <Fab
            onClick={() => setIsOpen(true)}
            aria-label="Abrir assistente de oração"
            sx={{
              backgroundColor: '#4A90D9',
              color: 'white',
              fontSize: '26px',
              width: 60,
              height: 60,
              '&:hover': { backgroundColor: '#357abd' },
            }}
          >
            🙏
          </Fab>
        </Tooltip>
      )}

      {isOpen && (
        <Fade in={isOpen}>
          <Paper
            elevation={8}
            sx={{
              width: { xs: 'calc(100vw - 48px)', sm: 340 },
              height: { xs: '80vh', sm: 500 },
              maxHeight: 500,
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            role="dialog"
            aria-label="Assistente de Oração"
          >
            {/* Header */}
            <Box
              sx={{
                backgroundColor: '#4A90D9',
                color: 'white',
                px: 2,
                py: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '20px', lineHeight: 1 }}>🙏</Typography>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2, fontSize: '13px' }}>
                    Assistente de Oração
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '10px' }}>
                    Fé Escrita
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setIsOpen(false)} size="small" sx={{ color: 'white' }} aria-label="Fechar">
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
            </Box>

            {/* Disclaimer */}
            <Box sx={{ backgroundColor: '#fff3cd', px: 1.5, py: 0.6, flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: '#856404', fontSize: '10px' }}>
                ⚠️ Este assistente oferece apoio espiritual e não substitui aconselhamento profissional de saúde mental.
              </Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.2, display: 'flex', flexDirection: 'column' }}>
              {messages.map(renderMessage)}

              {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Preparando uma oração especial para você...
                  </Typography>
                </Box>
              )}

              {currentPrayer && renderPrayerCard()}
              {phase === 'donation' && renderDonationPanel()}
              {phase === 'pix' && renderPixPanel()}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            {showInput && (
              <Box
                sx={{
                  p: 1.2,
                  borderTop: '1px solid #eee',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-end',
                  flexShrink: 0,
                }}
              >
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder="Digite sua necessidade de oração..."
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px', fontSize: '13px' } }}
                  inputProps={{ 'aria-label': 'Digite sua necessidade de oração' }}
                />
                <IconButton
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  aria-label="Enviar mensagem"
                  sx={{
                    backgroundColor: '#4A90D9',
                    color: 'white',
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    '&:hover': { backgroundColor: '#357abd' },
                    '&.Mui-disabled': { backgroundColor: '#ccc', color: 'white' },
                  }}
                >
                  <FontAwesomeIcon icon={faPaperPlane} size="sm" />
                </IconButton>
              </Box>
            )}
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default PrayerChatWidget;
